/**
 * Analytics helpers for practice sessions.
 *
 * These builders are the SINGLE source of the "scope" properties that describe where a practice
 * came from, so the three emission points of a volatile single-KC practice (`started` at launch,
 * `completed` on the summary and `discarded` on exit) always report the same session with the
 * same shape. There is one builder per moment, because what is known changes: at launch only the
 * just-created session is at hand, while the other two start from an already loaded session whose
 * read DTO resolves the origin KC. The loaded-session builder returns an empty object for anything
 * that is not a KC practice, so the tutor / student-generated / suggested flows keep their current
 * payload untouched.
 *
 * `kc_id` is ALWAYS the KC's `referenceId` (the curriculum reference, e.g. `M7-NyO-111`), never its
 * internal uuid: that is the value the rest of the frontend already reports as `kc_id` (see
 * `useChatbotPracticeView` and `PracticeExerciseControls`, which read `associatedKcReferenceId`).
 * Mixing both spaces of values inside one session would break the per-KC breakdown of the funnel,
 * so a KC that cannot be resolved as a `referenceId` is reported by OMITTING the property.
 */

import type { PracticeOriginKc } from '../types/practiceCourses';

/** The subset of a loaded practice session the scope properties can be derived from. */
export type PracticeScopeSessionSource = {
  /**
   * Origin KC as resolved by the read DTO (TANAI-2141) — the single source of the practised KC.
   * It survives an empty `sessionKCs` (early abandon) and a rollback, and it is `null` for every
   * session that is not a KC practice.
   */
  originKc?: PracticeOriginKc | null;
};

type BuildLoadedSessionScopePropsParams = {
  /**
   * Whether the loaded session is a volatile KC practice. Decided by the caller because it
   * combines the two signals of the read DTO: the session type and the resolved origin KC.
   */
  isKcPractice: boolean;
  /** Loaded session, the source of the practised KC. */
  sessionData?: PracticeScopeSessionSource | null;
};

/**
 * Scope properties of a volatile single-KC practice. `kc_id` is omitted (rather than sent
 * empty) when the practised KC cannot be resolved.
 */
export type PracticeScopeProps = {
  is_volatile?: boolean;
  kc_id?: string;
  kc_status?: 'blocked';
  origin?: 'mi_progreso_kc';
  scope?: 'kc';
};

/** The fixed scope of an "Áreas de mejora" practice, plus its KC when one is resolved. */
function buildKcScopeProps(kcReferenceId: string): PracticeScopeProps {
  const scopeProps: PracticeScopeProps = {
    is_volatile: true,
    kc_status: 'blocked',
    origin: 'mi_progreso_kc',
    scope: 'kc',
  };

  if (!kcReferenceId) return scopeProps;

  return { ...scopeProps, kc_id: kcReferenceId };
}

/**
 * Scope properties of the practice being LAUNCHED (`started`). Only the KC launch flow calls this
 * — it has just created the session — so there is no session type to check.
 *
 * `kc_id` comes from the `referenceId` of the origin KC that the create response of
 * POST /activities/sessions/kc resolves (TANAI-2141) — the same value the read DTO reports for
 * `completed` / `discarded`, so the three events of one practice share one space of values. It is
 * omitted (never sent as the internal uuid, which the create response also echoes flat as
 * `originKcId`) if the reference cannot be resolved.
 */
export function buildKcLaunchScopeProps(kcReferenceId?: string | null): PracticeScopeProps {
  return buildKcScopeProps(kcReferenceId ?? '');
}

/**
 * Scope properties of an ALREADY LOADED session (`completed`, `discarded`): the volatile KC
 * practice is reported with its origin, scope, volatility, KC and KC status; any other session
 * returns `{}` so no other flow changes its payload.
 *
 * The practised KC comes from `originKc.referenceId`, which the read DTO resolves from the
 * session's origin: it is therefore correct even when the session has no answers yet and after a
 * rollback has added other KCs to `sessionKCs`.
 */
export function buildLoadedSessionScopeProps({ isKcPractice, sessionData }: BuildLoadedSessionScopePropsParams): PracticeScopeProps {
  if (!isKcPractice) return {};

  return buildKcScopeProps(sessionData?.originKc?.referenceId ?? '');
}
